import styles from './style.module.scss';
import { UserBonusCheckpointsV2RenderStepProps } from './props';
import {
  getUserBonusCheckpointDescription,
  getUserBonusCheckpointLabel,
} from '../../../helper';
import { Button, CircularProgress } from '@starsoft/common/components';
import { getBonusTypeColor } from '@/components/Bonus/UserBonus/Card/helper';
import { getComputedColor, getMoneyOrAmount } from '@starsoft/common/utils';
import useTranslation from 'next-translate/useTranslation';
import { useUserBonusCheckpoint } from '@/hooks/user-bonus/useUserBonusCheckpoint';
import UserBonusCheckpointsV2RenderStepSkeleton from './Skeleton';
import { UserBonusCheckpointStatus } from '@/enums/userBonusCheckpointStatus';
import { getContrastColor } from '@/utils/color/getContrastColor';
import { WalletService } from '@starsoft/common/services';
import { BonusTypeType } from '@/enums/bonusTypeId';

export default function UserBonusCheckpointsV2RenderStep({
  checkpoint,
  userBonus,
  isCurrentCheckpoint,
  isLoading,
  position,
}: UserBonusCheckpointsV2RenderStepProps) {
  const { t, lang } = useTranslation('bonus');
  const { animate, handleClaimBonus, loadingClaimBonus, lottieAnimationRef } =
    useUserBonusCheckpoint(checkpoint, userBonus);
  const label = getUserBonusCheckpointLabel(
    checkpoint,
    loadingClaimBonus,
    isCurrentCheckpoint,
  );
  const description = getUserBonusCheckpointDescription(
    checkpoint,
    isCurrentCheckpoint,
  );

  if (isLoading) {
    return <UserBonusCheckpointsV2RenderStepSkeleton />;
  }

  return (
    <div
      className={`${styles.container}`}
      onClick={handleClaimBonus}
      style={{
        //@ts-ignore
        '--progress': `${getMoneyOrAmount(checkpoint?.progress)}%`,
        '--delay': `${position * 2.2}s`,
        '--bonus-color': getBonusTypeColor(
          userBonus?.bonus?.type?.id as BonusTypeType,
        ),
        '--contrast-bonus-color': getContrastColor(
          getBonusTypeColor(userBonus?.bonus?.type?.id as BonusTypeType),
        ),
      }}
    >
      <CircularProgress
        pathColor={getComputedColor('--card-color')}
        color={getBonusTypeColor(userBonus?.bonus?.type?.id as BonusTypeType)}
        trailColor={getBonusTypeColor(
          userBonus?.bonus?.type?.id as BonusTypeType,
        )}
        progress={getMoneyOrAmount(checkpoint?.progress)}
        className={`${styles.container__indicator} ${styles[`container__indicator--${isCurrentCheckpoint ? 'current' : checkpoint?.status}`]}`}
      >
        {animate && (
          <div className={styles.container__lottie} ref={lottieAnimationRef} />
        )}
        <div className={styles.container__item}>{label}</div>
      </CircularProgress>
      <div className={styles.container__column}>
        <div className={styles.container__column__label}>
          {WalletService.maskCurrency({
            amount: getMoneyOrAmount(checkpoint?.amount),
            coin: userBonus?.coin,
          })}
        </div>
        <div className={styles.container__column__description}>
          {t(description, {
            wagered: WalletService.maskCurrency({
              amount: getMoneyOrAmount(checkpoint?.wagered),
              coin: userBonus?.coin,
            }),
            total: WalletService.maskCurrency({
              amount: getMoneyOrAmount(checkpoint?.total),
              coin: userBonus?.coin,
            }),
            date: !!checkpoint?.claimedAt
              ? Intl.DateTimeFormat(lang, {
                  day: '2-digit',
                  month: '2-digit',
                  year: '2-digit',
                }).format(new Date(checkpoint?.claimedAt))
              : '',
          })}
        </div>
      </div>

      <Button
        isTiny
        useMaxContent
        disabled={checkpoint?.status != UserBonusCheckpointStatus.Fulfilled}
        className={styles.container__button}
      >
        {t(
          checkpoint?.status == UserBonusCheckpointStatus.Claimed
            ? 'claimed_label'
            : 'claim_label',
        )}
      </Button>
    </div>
  );
}
